import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Shield, Loader2, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProtectionDepositCard({ clubId, clubName, contributionAmount, onDepositComplete }) {
  const [depositStatus, setDepositStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customAmount, setCustomAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadDepositStatus();
  }, [clubId]);

  const loadDepositStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/protection-deposits/${clubId}`,
        { withCredentials: true }
      );
      setDepositStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Load deposit status error:', error);
      // If no deposit exists yet, set default
      setDepositStatus({
        deposit_balance: 0,
        required_amount: contributionAmount,
        is_sufficient: false,
        deficit: contributionAmount
      });
      setLoading(false);
    }
  };

  const handleAddDeposit = async (amount) => {
    if (!amount || amount < 0.10) {
      toast.error('Minimum deposit is $0.10');
      return;
    }
    if (amount > 1000) {
      toast.error('Maximum deposit is $1000');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/protection-deposits/add`,
        {
          club_id: clubId,
          amount: parseFloat(amount),
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Add deposit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process deposit');
      setProcessing(false);
    }
  };

  const progressPercent = depositStatus 
    ? Math.min(100, (depositStatus.deposit_balance / depositStatus.required_amount) * 100)
    : 0;

  if (loading) {
    return (
      <Card className="border-stone-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="protection-deposit-card"
    >
      <Card className={`border-2 ${depositStatus?.is_sufficient ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-lg">
            <Shield className={`w-5 h-5 mr-2 ${depositStatus?.is_sufficient ? 'text-green-600' : 'text-amber-600'}`} />
            Protection Deposit
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Info Box */}
          <div className="bg-white rounded-lg p-3 mb-4 border border-stone-200">
            <div className="flex items-start">
              <Info className="w-4 h-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-stone-600">
                A protection deposit equal to one contribution cycle is required before joining. 
                This secures the group if you miss a payment and will be refunded when you leave the club.
              </p>
            </div>
          </div>

          {/* Status Display */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-stone-600">Your Deposit</span>
              <span className="font-semibold text-stone-900">
                ${depositStatus?.deposit_balance?.toFixed(2) || '0.00'} / ${depositStatus?.required_amount?.toFixed(2)}
              </span>
            </div>
            <Progress 
              value={progressPercent} 
              className={`h-3 ${depositStatus?.is_sufficient ? 'bg-green-100' : 'bg-amber-100'}`}
            />
          </div>

          {/* Status Message */}
          {depositStatus?.is_sufficient ? (
            <motion.div 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="flex items-center p-3 bg-green-100 rounded-lg mb-4"
            >
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm text-green-800 font-medium">
                Deposit complete! You can join this club.
              </span>
            </motion.div>
          ) : (
            <div className="flex items-center p-3 bg-amber-100 rounded-lg mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 mr-2" />
              <span className="text-sm text-amber-800">
                Add ${depositStatus?.deficit?.toFixed(2)} more to join this club
              </span>
            </div>
          )}

          {/* Quick Add Buttons */}
          {!depositStatus?.is_sufficient && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="add-full-deposit-btn"
                  onClick={() => handleAddDeposit(depositStatus?.deficit)}
                  disabled={processing}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    `Add Full: $${depositStatus?.deficit?.toFixed(2)}`
                  )}
                </Button>
                <Button
                  data-testid="add-partial-deposit-btn"
                  onClick={() => handleAddDeposit(Math.min(depositStatus?.deficit, 10))}
                  disabled={processing}
                  variant="outline"
                  className="rounded-xl border-stone-200"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    `Add $${Math.min(depositStatus?.deficit, 10).toFixed(2)}`
                  )}
                </Button>
              </div>

              {/* Custom Amount */}
              <div className="flex gap-2">
                <Input
                  data-testid="custom-deposit-input"
                  type="number"
                  min="0.10"
                  max="1000"
                  step="0.01"
                  placeholder="Custom amount"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="rounded-xl"
                />
                <Button
                  data-testid="add-custom-deposit-btn"
                  onClick={() => handleAddDeposit(parseFloat(customAmount))}
                  disabled={processing || !customAmount}
                  variant="outline"
                  className="rounded-xl px-6"
                >
                  Add
                </Button>
              </div>
            </div>
          )}

          {/* Already Sufficient - Top Up Option */}
          {depositStatus?.is_sufficient && (
            <div className="pt-2 border-t border-stone-200">
              <p className="text-xs text-stone-500 mb-2">Want to add extra buffer?</p>
              <div className="flex gap-2">
                <Input
                  data-testid="topup-deposit-input"
                  type="number"
                  min="0.10"
                  max="1000"
                  step="0.01"
                  placeholder="Top up amount"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="rounded-xl"
                />
                <Button
                  data-testid="topup-deposit-btn"
                  onClick={() => handleAddDeposit(parseFloat(customAmount))}
                  disabled={processing || !customAmount}
                  variant="outline"
                  className="rounded-xl px-6"
                >
                  Top Up
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
